import 'rc-slider/assets/index.css';

import React, { useState, useCallback, createRef, useEffect } from 'react';

import { message, Spin, Tooltip, Radio } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPause,
  faPlay,
  faRedoAlt,
  faUndoAlt,
} from '@fortawesome/free-solid-svg-icons';
import trashIcon from '../../studio/assets/trashIcon.svg';
import Slider from 'rc-slider';
import ReactPlayer from 'react-player';

import {
  AUDIO_LIBRARY_BUCKET,
  calculatePlayerTimeSeek,
  formatSeconds,
  getAudioDuration,
  getSignedUrl,
  LibraryState,
} from '../utils';
import { UserAudioLibrary } from '../../graphql/types';

import styles from './GiideWithStep.module.css';
import { LoadingOutlined } from '@ant-design/icons';
import confirm from 'antd/es/modal/confirm';
import { deleteFromAudioLibrary } from '../../actions';
import useMutation from '../../hooks/useMutation';
import {
  DeleteUserAudioLibraryInput,
  DeleteUserAudioLibraryMutation,
} from '../../graphql/api';
import gql from 'graphql-tag';
import { useDispatch } from 'react-redux';

interface IProps {
  item: UserAudioLibrary;
  selectedId: string | null;
  playingId: string | null;
  setSelectedId: (id: string | null) => void;
  setPlayingId: (id: string | null) => void;
}

const deleteUserAudioLibrary = gql`
  mutation DeleteUserAudioLibrary($input: DeleteUserAudioLibraryInput!) {
    deleteUserAudioLibrary(input: $input) {
      id
    }
  }
`;

const loadingIcon = <LoadingOutlined className="giideLoadingIcon" spin />;

const AudioLibraryItems: React.FC<IProps> = ({
  item,
  selectedId,
  playingId,
  setSelectedId,
  setPlayingId,
}) => {
  const dispatch = useDispatch();
  const [playing, setPlaying] = useState(false);
  const [progressTime, setProgressTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState<number>(0); // in seconds
  const [audioUrl, setAudioUrl] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [loading, setLoading] = React.useState<boolean>(false);

  const libraryPlayerRef = createRef<ReactPlayer>();
  const titleRef = createRef<HTMLDivElement>();

  const deleteAudioLibraryMutation = useMutation<
    DeleteUserAudioLibraryInput,
    DeleteUserAudioLibraryMutation
  >(deleteUserAudioLibrary);

  const isTitleOverflowed = useCallback(() => {
    if (titleRef.current) {
      const overflowDetected =
        titleRef.current.offsetWidth !== titleRef.current.scrollWidth;
      setShowTooltip(overflowDetected);
    }
  }, [titleRef]);

  const onDeleteAudio = React.useCallback(
    deleteId => {
      confirm({
        centered: true,
        closable: true,
        className: 'deleteModal deleteAudioModal',
        cancelText: 'Cancel',
        okText: 'Delete',
        onOk: async () => {
          try {
            await deleteAudioLibraryMutation.callMutation({ id: deleteId });
            // const filePath = data?.find(f => f?.id === deleteId)?.audioUrl;
            // if (filePath) {
            //   await Storage.remove(filePath, {
            //     customPrefix: { public: '' },
            //   }).catch(e => {});
            // }
            dispatch(deleteFromAudioLibrary(deleteId));
          } catch (err) {
            message.error('Failed to delete the audio');
          }
          return true;
        },
        title:
          'Are you sure that you want to delete this recording from your library?',
      });
    },
    [dispatch, deleteAudioLibraryMutation]
  );

  useEffect(() => {
    window.addEventListener('resize', isTitleOverflowed, false);
    isTitleOverflowed();
    return () => {
      window.removeEventListener('resize', isTitleOverflowed, false);
    };
  }, [isTitleOverflowed, titleRef]);

  const generateAudioUrl = useCallback(
    async (id: string, filePath: string | null) => {
      if (filePath) {
        // Storage.configure({
        //   AWSS3: {
        //     bucket: AUDIO_LIBRARY_BUCKET,
        //   },
        // });
        setLoading(true);
        const data = await getSignedUrl(filePath, AUDIO_LIBRARY_BUCKET)
          // var url = await Storage.get(filePath, {
          //   customPrefix: { public: '' },
          // })
          .catch(() => message.error('Could not load the audio file.'));
        // Storage.configure({
        //   AWSS3: {
        //     bucket: RECORDINGS_BUCKET,
        //   },
        // });
        if (data === true || data === false) {
          return;
        }

        setAudioUrl(data.url as string);
        setPlayingId(id);
        setPlaying(true);
        setProgressTime(0);
        setLoading(false);
        const duration = await getAudioDuration(data.url as string);
        setTotalDuration(duration);
      }
    },
    [setPlayingId, setPlaying]
  );

  const seekTo = useCallback(
    async (delta: number) => {
      const adjustedTime = calculatePlayerTimeSeek(
        delta,
        progressTime,
        totalDuration
      );
      setProgressTime(adjustedTime);
      libraryPlayerRef.current?.seekTo(adjustedTime / 1000);
    },
    [progressTime, setProgressTime, libraryPlayerRef, totalDuration]
  );

  let date = '';
  if (item?.createdAt) {
    const now = new Date(item?.createdAt);
    date = now.getMonth() + 1 + '/' + now.getDate() + '/' + now.getFullYear();
  }

  const isRecentlyUpdated =
    item?.updatedAt && Date.parse(item.updatedAt) > Date.now() - 1000 * 30;

  return !item || item.state === 'error' ? null : (
    <li>
      <div className={styles.audioLibraryItem}>
        <div>
          {item.state === 'finished' && !loading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Radio
                // className={styles.itemRadioBtn}
                checked={!!(selectedId && selectedId === item.id)}
                onClick={() => {
                  if (selectedId && selectedId === item.id) {
                    setSelectedId(null);
                  } else {
                    setSelectedId(item.id);
                  }
                }}
              />
              <FontAwesomeIcon
                icon={playing && playingId === item.id ? faPause : faPlay}
                className={styles.greenIcon}
                onClick={() => {
                  if (playingId && playingId === item.id) {
                    // setPlayingId(null);
                    setPlaying(!playing);
                    // setProgressTime(0);
                  } else if (item.audioUrl) {
                    generateAudioUrl(item.id, item.audioUrl);
                  }
                }}
              />
            </div>
          )}
          {loading && (
            <Spin
              indicator={loadingIcon}
              className={styles.libraryItemLoader}
            />
          )}
          {item.state !== 'finished' && (
            <FontAwesomeIcon
              icon={faPlay}
              className={styles.disabledPlayOnUpload}
            />
          )}
        </div>
        <Tooltip title={showTooltip ? item.title : undefined} placement="right">
          <div
            ref={titleRef}
            className={styles.placeholderMainArea}
            onClick={() => {
              if (playingId && playingId === item.id) {
                setPlayingId(null);
                setPlaying(false);
                setProgressTime(0);
              } else if (item.audioUrl) {
                generateAudioUrl(item.id, item.audioUrl);
              }
            }}
          >
            {item?.title}
          </div>
        </Tooltip>

        <div className={styles.rightArea}>
          <div className={styles.greyText}>{date}</div>
          <div className={styles.greyText}>
            {item?.durationSeconds ? formatSeconds(item.durationSeconds) : ''}
          </div>
          {(item.state === 'uploaded' || item.state === 'error') && (
            <div className={styles.stateText}>
              {LibraryState[item.state]}
              {item.state === 'uploaded' && (
                <div className="numberCircleSpin" />
              )}
            </div>
          )}

          {(item.state === 'finished' ||
            ((item.state === 'uploaded' || item.state === 'error') &&
              !isRecentlyUpdated)) && (
            <>
              {/* <Button
                className={`${styles.selectBtn} ${
                  item.id === selectedId ? styles.selectBtnActive : ''
                }`}
                onClick={() => {
                  if (selectedId && selectedId === item.id) {
                    setSelectedId(null);
                  } else {
                    setSelectedId(item.id);
                  }
                }}
              >
                Next
              </Button> */}
              <img
                src={trashIcon}
                alt="upload icon"
                onClick={() => onDeleteAudio(item.id)}
              />
            </>
          )}
        </div>
      </div>
      <div>
        {playingId && playingId === item.id && item.durationSeconds && (
          <div className={styles.libraryPlayerRow}>
            <div className={`libraryPlayer ${styles.playerWrapper}`}>
              <Slider
                value={progressTime}
                onChange={value => {
                  setProgressTime(value);
                  libraryPlayerRef.current?.seekTo(value / 1000, 'seconds');
                }}
                max={Math.round(item.durationSeconds * 1000)}
                step={1}
              />
              <div className={styles.playerControls}>
                <div>{formatSeconds(progressTime / 1000)}</div>
                <div>
                  <FontAwesomeIcon
                    icon={faUndoAlt}
                    onClick={() => seekTo(-10000)}
                  />
                  <FontAwesomeIcon
                    icon={playing ? faPause : faPlay}
                    className={styles.greenIcon}
                    onClick={() => setPlaying(!playing)}
                  />
                  <FontAwesomeIcon
                    icon={faRedoAlt}
                    onClick={() => seekTo(10000)}
                  />
                </div>
                <div>{formatSeconds(item.durationSeconds)}</div>
              </div>
              <ReactPlayer
                ref={libraryPlayerRef}
                url={audioUrl}
                playing={playing}
                progressInterval={20}
                onProgress={({ playedSeconds }) =>
                  setProgressTime(playedSeconds * 1000)
                }
                onEnded={() => {
                  libraryPlayerRef.current?.seekTo(0);
                  setPlaying(false);
                }}
                width={0}
                height={0}
                onBuffer={() => setLoading(true)}
                onBufferEnd={() => setLoading(false)}
              />
            </div>
          </div>
        )}
      </div>
    </li>
  );
};

export default AudioLibraryItems;
